import { dateFromValue } from './date';

export function normalizeReservationStatus(value) {
  const key = String(value || 'Query').trim().toLowerCase();
  if (key === 'reserved') return 'reserved';
  if (key === 'cancelled' || key === 'canceled') return 'cancelled';
  if (key === 'completed') return 'completed';
  return 'query';
}

export function normalizeAdvanceStatus(value) {
  const key = String(value || 'Pending').trim().toLowerCase();
  if (key === 'paid') return 'paid';
  if (key.includes('pay') && key.includes('arrival')) return 'pay_on_arrival';
  if (key.includes('waived') || key.includes('regular guest')) return 'advance_waived_regular_guest';
  if (key === 'refunded') return 'refunded';
  return 'pending';
}

export function advanceStatusLabel(value) {
  return {
    pending: 'Pending',
    paid: 'Paid',
    refunded: 'Refunded',
    pay_on_arrival: 'Pay on Arrival',
    advance_waived_regular_guest: 'Advance Waived (Regular Guest)'
  }[normalizeAdvanceStatus(value)] || value || 'Pending';
}

export function reservationStateKey(value, actualPaxRaw = '') {
  const raw = String(value || '').trim();
  const key = raw.toLowerCase().replace(/[^a-z]/g, '');
  const actualRaw = String(actualPaxRaw || '').toLowerCase();
  if (key.startsWith('onfloor') || key === 'inhouse') return 'onfloor';
  if (key === 'served' || key.startsWith('served')) return 'served';
  if (key === 'noshows' || key === 'noshow') return 'noshow';
  if (key === 'reservations' || key === 'reservation') return 'reservations';
  if (actualRaw.startsWith('served:')) return 'served';
  if (actualRaw.includes('no show') || actualRaw.includes('no-show')) return 'noshow';
  if ((parseFloat(actualRaw.replace(/[^0-9.]/g, '')) || 0) > 0) return 'onfloor';
  return 'reservations';
}

export function billingPax({ adults = 0, kids57 = 0 } = {}) {
  return Number(adults || 0) + (Number(kids57 || 0) * 0.5);
}

export function reservedPax(fields = {}) {
  const storedPax = parseFloat(fields['No. Of PAX']) || 0;
  const adults = parseFloat(fields.Adults ?? fields.Men) || 0;
  const kids57 = parseFloat(fields['Kids (5-7 Yrs)'] ?? fields.Kids) || 0;
  return billingPax({ adults, kids57 }) || storedPax;
}

export function attachmentUrl(file = {}) {
  const url = file.lgThumbnailUrl || file.smThumbnailUrl || file.presignedUrl || '';
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/')) return `https://teable.namuve.com${url}`;
  return `https://teable.namuve.com/${url}`;
}

export function normalizeReservation(record = {}) {
  const fields = record.fields || {};
  const bookingDate = dateFromValue(fields['Booking Date']);
  const createdDate = dateFromValue(fields['Reservation Created Date'] || record.createdTime);
  const name = String(fields.Name || '').trim();
  const number = String(fields.Number || '').trim();
  const adults = parseFloat(fields.Adults ?? fields.Men) || 0;
  const kids57 = parseFloat(fields['Kids (5-7 Yrs)'] ?? fields.Kids) || 0;
  const kidsUnder5 = parseFloat(fields['Kids (<5 Yrs)']) || 0;
  const actualAdults = parseFloat(fields['Actual Adults'] ?? fields['Actual Men']) || 0;
  const actualKids57 = parseFloat(fields['Actual Kids (5-7 Yrs)']) || 0;
  const actualKidsUnder5 = parseFloat(fields['Actual Kids (<5 Yrs)'] ?? fields['Actual Kids']) || 0;
  const actualPaxRaw = String(fields['Actual No. Of PAX'] || '').trim();
  const stage = reservationStateKey(fields['Reservation ID State '] || fields['Reservation ID State'], actualPaxRaw);
  const actualHeadcount = actualAdults + actualKids57 + actualKidsUnder5;
  const actualRawNumber = parseFloat(actualPaxRaw.replace(/[^0-9.]/g, '')) || 0;
  const billablePax = parseFloat(fields['Billing PAX']) || billingPax({ adults: actualAdults, kids57: actualKids57 }) || 0;
  const actualGuestCount = stage === 'noshow' ? 0 : actualRawNumber || actualHeadcount || 0;
  const attachments = Array.isArray(fields.Attachment) ? fields.Attachment : [];
  const slot = String(fields.Slot || '').trim();
  const branch = String(fields.Branch || '').trim();
  const reservationId = fields['Reservation ID'] ? String(fields['Reservation ID']).trim() : '';

  return {
    raw: record,
    fields,
    id: record.id || '',
    recordName: record.name || '',
    name: name || 'Unknown Caller',
    phone: number,
    reservationId,
    hostawayId: reservationId,
    bookingDate,
    checkin: bookingDate,
    createdDate,
    reservationDate: createdDate,
    branch,
    channel: branch.toLowerCase().replace(/\s+/g, '-'),
    slot,
    status: normalizeReservationStatus(fields['Reservation Status']),
    reservationStatus: fields['Reservation Status'] || 'Query',
    advanceStatus: normalizeAdvanceStatus(fields['Advance Status']),
    advanceStatusLabel: advanceStatusLabel(fields['Advance Status']),
    advAmount: parseFloat(fields['Adv Amount']) || 0,
    guestCount: reservedPax(fields),
    adults,
    kids57,
    kidsUnder5,
    kids: kids57 + kidsUnder5,
    actualGuestCount,
    billingPax: billablePax,
    actualAdults,
    actualKids57,
    actualKidsUnder5,
    actualStage: stage,
    reservationState: fields['Reservation ID State '] || fields['Reservation ID State'] || '',
    reservedFloor: String(fields['Reserved Floor'] || '').trim(),
    actualFloor: String(fields['Actual Floor'] || '').trim(),
    reservationType: fields['Reservation Type'] || '',
    reservationBy: fields['Reservation by'] || fields['Reservation By'] || '',
    invoiceNo: fields['Invoice No.'] || '',
    advanceConfirmationStatus: fields['Advance Confirmation Status'] || fields.advanceConfirmationStatus || 'Pending',
    advanceConfirmedBy: fields['Advance Confirmed By'] || fields.advanceConfirmedBy || '',
    advanceConfirmedAt: fields['Advance Confirmed At'] || fields.advanceConfirmedAt || '',
    advanceReferenceNumber: fields['Advance Reference Number'] || fields.advanceReferenceNumber || '',
    advanceAccountCode: fields['Advance Account Code'] || fields.advanceAccountCode || '',
    odooAdvanceSyncStatus: fields['Odoo Advance Sync Status'] || fields.odooAdvanceSyncStatus || '',
    odooAdvanceMoveId: fields['Odoo Advance Move ID'] || fields.odooAdvanceMoveId || '',
    odooAdvanceEntryReference: fields['Odoo Advance Entry Reference'] || fields.odooAdvanceEntryReference || '',
    odooAdvanceSyncMessage: fields['Odoo Advance Sync Message'] || fields.odooAdvanceSyncMessage || '',
    advanceReceivedAmount: parseFloat(fields['Advance Received Amount'] ?? fields.advanceReceivedAmount) || 0,
    advanceRemarks: fields['Advance Remarks'] || fields.advanceRemarks || '',
    advanceHandling: fields['Advance Handling'] || fields.advanceHandling || '',
    refundStatus: fields['Refund Status'] || fields.refundStatus || 'Pending',
    refundRequestedAt: fields['Refund Requested At'] || fields.refundRequestedAt || '',
    refundProcessedBy: fields['Refund Processed By'] || fields.refundProcessedBy || '',
    refundProcessedAt: fields['Refund Processed At'] || fields.refundProcessedAt || '',
    refundAmount: parseFloat(fields['Refund Amount'] ?? fields.refundAmount) || 0,
    refundRemarks: fields['Refund Remarks'] || fields.refundRemarks || '',
    holdRemarks: fields['Hold Remarks'] || fields.holdRemarks || '',
    bankName: fields['Bank Name'] || fields.bankName || '',
    accountNumberOrIBAN: fields['Account Number / IBAN'] || fields.accountNumberOrIBAN || '',
    accountTitle: fields['Account Title'] || fields.accountTitle || '',
    refundAttachment: Array.isArray(fields['Refund Attachment']) ? fields['Refund Attachment'] : (fields.refundAttachment || ''),
    vip: fields.vip === true || String(fields.vip || fields.VIP || '').toLowerCase() === 'true',
    specialInstructions: fields.specialInstructions || fields['Special Instructions'] || '',
    cancellationReason: fields['Cancellation Reason'] || '',
    attachments,
    attachmentUrl: attachmentUrl(attachments[0]),
    cityArea: fields['City / Area'] || '',
    lastModifiedTime: fields['Last modified time'] || record.lastModifiedTime || ''
  };
}

export function normalizeReservations(records = []) {
  return records.map(normalizeReservation);
}
